// Pestaña "Cobro" del HCE (Fase 1, orden de cobro — tasks/orden-cobro/PLAN.md
// §4.1, §5 PR 5). El médico arma la orden de la atención en curso y la envía
// a caja — COBRAR (perfiles de facturación, forma de pago, vuelto) vive en
// /admin/cobrar del CRM, no acá. Carrito calcado de OrdenesFarmacia.tsx,
// resolución de patientId calcada de SeguimientoPacienteTab.tsx.
//
// Fork por familia (Fase 2, ítem 9, primer uso real de lib/family-overrides.ts
// del PR 8): "demo" edita precio y descuenta directo, sin tope — ver
// CobroTab.demo.tsx. Esta vista (`CobroTabGenerico`, exportada para que el
// fork la reuse si hace falta) es la que sirve HOY a las demás familias
// (facilitaclinic/multiples-sedes/almacenes-espana) sin ningún cambio — no
// tiene edición de precio ni descuento todavía (llega en el PR 9b, con tope).
import type { JSX } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router';
import { X } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { obtenerMisEspecialidades, type EspecialidadHce } from '@/lib/hceVault';
import {
  actualizarLineasAtencion,
  anularAtencion,
  crearOObtenerAtencion,
  conConsultaPrecargada,
  guardarYEnviarACaja,
  lineaConsultaBloqueada,
  listarCatalogoCobro,
  listarLotesDelEspejo,
  listarOrdenesDeCobroDePaciente,
  type CatalogoItemCobro,
  type OrdenAtencionCobro,
  precargaFallo,
} from '@/lib/cobro';
import { useCotizacionCobro } from '@/lib/useCotizacionCobro';
import { useTenantActual } from '@/auth/useTenantActual';
import { vistaDeFamilia } from '@/lib/family-overrides';
import type { PatientChartOutletContext } from './PatientChartPage';
import { LineaConsultaBloqueada } from './LineaConsultaBloqueada';
import { obtenerPacienteVinculado } from './vaultClient';

function formatoUsd(cents: number): string {
  return (cents / 100).toLocaleString('es-EC', { style: 'currency', currency: 'USD' });
}

type EstadoContacto =
  | { fase: 'cargando' }
  | { fase: 'sin-contacto' }
  | { fase: 'listo'; patientId: number; phone: string }
  | { fase: 'error'; mensaje: string };

type LineaCarrito = {
  itemId: number;
  cantidad: string;
  /** Botiquín de servicio (Fase 2, ítem 12b) — sin exigir al Guardar, solo al "Enviar a caja". */
  administracionDosis?: string;
  administracionVia?: string;
  administracionLotCode?: string;
};

export function CobroTabGenerico(): JSX.Element {
  const { t } = useTranslation();
  const outlet = useOutletContext<PatientChartOutletContext | undefined>();
  const patient = outlet?.patient;

  const [contacto, setContacto] = useState<EstadoContacto>({ fase: 'cargando' });
  const [especialidades, setEspecialidades] = useState<EspecialidadHce[] | null>(null);
  const [specialtyId, setSpecialtyId] = useState('');

  const [orden, setOrden] = useState<OrdenAtencionCobro | null>();
  const [catalogo, setCatalogo] = useState<CatalogoItemCobro[]>([]);
  const [historial, setHistorial] = useState<OrdenAtencionCobro[]>([]);

  const [lineas, setLineas] = useState<LineaCarrito[]>([]);
  const [lotesPorItem, setLotesPorItem] = useState<Record<number, { id: number; lotCode: string; expiresAt: string | null; quantity: number }[]>>({});
  const [busquedaItem, setBusquedaItem] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [creandoAtencion, setCreandoAtencion] = useState(false);
  const [anulando, setAnulando] = useState(false);
  const [motivoAnulacion, setMotivoAnulacion] = useState('');
  const [anulandoAbierto, setAnulandoAbierto] = useState(false);
  const [error, setError] = useState<string>();
  const [aviso, setAviso] = useState<string>();

  useEffect(() => {
    if (!patient) return;
    let cancelado = false;
    setContacto({ fase: 'cargando' });
    // Reinicio explícito de todo lo que cuelga del paciente anterior — sin
    // esto, si PatientChartPage no remonta al cambiar de `:id` (no hay
    // `key` en la ruta), la atención/carrito/historial del paciente
    // anterior seguían visibles y operables (Guardar/Enviar/Anular)
    // mientras la ficha ya mostraba al paciente nuevo. Hallado por el
    // revisor de este PR.
    setOrden(undefined);
    setLineas([]);
    setCatalogo([]);
    setHistorial([]);
    setBusquedaItem('');
    setError(undefined);
    setAviso(undefined);
    setAnulandoAbierto(false);
    setMotivoAnulacion('');
    const cedula = patient.identifier?.[0]?.value;
    if (!cedula) {
      setContacto({ fase: 'sin-contacto' });
      return;
    }
    obtenerPacienteVinculado(cedula)
      .then((vinculado) => {
        if (cancelado) return;
        if (!vinculado?.patientId) {
          setContacto({ fase: 'sin-contacto' });
          return;
        }
        setContacto({ fase: 'listo', patientId: vinculado.patientId, phone: vinculado.phone ?? '' });
      })
      .catch((err: unknown) => {
        if (!cancelado) setContacto({ fase: 'error', mensaje: err instanceof Error ? err.message : String(err) });
      });
    return () => {
      cancelado = true;
    };
  }, [patient]);

  useEffect(() => {
    obtenerMisEspecialidades()
      .then((r) => {
        setEspecialidades(r);
        if (r.length === 1) setSpecialtyId(r[0]!.id);
      })
      .catch(() => setEspecialidades([]));
  }, []);

  async function cargarHistorial(patientId: number): Promise<void> {
    const r = await listarOrdenesDeCobroDePaciente(patientId);
    if (!r.disponible) setError((previo) => previo ?? t('cobroPaciente.historialNoDisponible'));
    setHistorial(r.disponible ? r.ordenes : []);
  }

  // Atención en curso: get-or-create automático apenas se conoce el
  // paciente y (si hace falta elegir) la especialidad — mismo criterio que
  // el resto del HCE (nunca un paso extra si no hace falta). No se repite
  // sola tras "Enviar a caja"/"Anular" — eso es explícito (§4.1 "Agregar
  // orden adicional"), ver iniciarAtencion().
  useEffect(() => {
    if (contacto.fase !== 'listo') return;
    if (especialidades === null) return;
    if (especialidades.length > 1 && !specialtyId) return;
    if (orden !== undefined) return;
    void iniciarAtencion(contacto.patientId, contacto.phone);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- solo debe correr una vez que se conocen paciente+especialidad; iniciarAtencion no cambia de identidad relevante acá
  }, [contacto, especialidades, specialtyId, orden]);

  async function iniciarAtencion(patientId: number, phone: string): Promise<void> {
    setCreandoAtencion(true);
    setError(undefined);
    try {
      const r = await crearOObtenerAtencion({ patientId, patientPhone: phone || undefined, specialtyId: specialtyId || undefined });
      if (!r.ok) {
        setError(r.error);
        setOrden(null);
        return;
      }
      setOrden(r.datos.orden);
      // Sala CRM↔HCE 5b: la consulta precargada por el servidor no es del carrito (no se edita); se muestra bloqueada y viaja aparte al guardar/enviar.
      if (precargaFallo(r.datos.precarga)) setError(t('cobroPaciente.precargaError'));
      const delCarrito = r.datos.orden.lines.filter((l) => l.origenLinea !== 'consulta_cita');
      setLineas(
        delCarrito.map((l) => ({
          itemId: l.itemId,
          cantidad: String(l.quantity),
          administracionDosis: l.administracionDosis ?? '',
          administracionVia: l.administracionVia ?? '',
          administracionLotCode: l.administracionLotCode ?? '',
        })),
      );
      const cat = await listarCatalogoCobro(specialtyId || undefined, r.datos.orden.id);
      if (!cat.disponible) setError(t('cobroPaciente.catalogoNoDisponible'));
      setCatalogo(cat.items.filter((i) => i.active));
      await cargarHistorial(patientId);
    } finally {
      setCreandoAtencion(false);
    }
  }

  const catalogoPorId = new Map(catalogo.map((i) => [i.id, i]));
  const consulta = lineaConsultaBloqueada(orden);
  const lineasValidas = lineas
    .map((l) => ({
      itemId: l.itemId,
      quantity: Number(l.cantidad),
      administracionDosis: l.administracionDosis,
      administracionVia: l.administracionVia,
      administracionLotCode: l.administracionLotCode,
    }))
    .filter((l) => Number.isInteger(l.quantity) && l.quantity > 0);
  const hayCantidadesInvalidas = lineasValidas.length < lineas.length;
  const totalCarritoCents = lineasValidas.reduce((suma, l) => {
    const item = catalogoPorId.get(l.itemId);
    return suma + (item ? item.amountCents * l.quantity : 0);
  }, consulta?.lineTotalCents ?? 0);
  // Las líneas que viajan al servidor: la consulta precargada IDÉNTICA más el carrito (el servidor rechaza un carrito que no la traiga).
  const lineasAEnviar = conConsultaPrecargada(orden, lineasValidas);
  // Botiquín de servicio (Fase 2, ítem 12b): registro de administración
  // obligatorio antes de "Enviar a caja" (sin exigirlo al Guardar) — el
  // server lo revalida igual en `POST .../enviar`.
  const lineasBotiquinSinAdministracion = lineasValidas.filter((l) => {
    const item = catalogoPorId.get(l.itemId);
    return item?.farmaciaItemId != null && (!l.administracionDosis?.trim() || !l.administracionVia?.trim() || !l.administracionLotCode?.trim());
  });

  const totalServidorCents = useCotizacionCobro(lineasAEnviar, specialtyId || undefined);

  useEffect(() => {
    for (const l of lineas) {
      const item = catalogoPorId.get(l.itemId);
      if (item?.farmaciaItemId != null && !lotesPorItem[l.itemId]) {
        listarLotesDelEspejo(l.itemId).then((lotes) => setLotesPorItem((prev) => ({ ...prev, [l.itemId]: lotes })));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- solo dispara para líneas nuevas de botiquín sin lotes cargados aún; catalogoPorId/lotesPorItem cambiarían la identidad en cada render
  }, [lineas, catalogo]);

  const textoItem = busquedaItem.trim().toLowerCase();
  const enCarrito = new Set([...lineas.map((l) => l.itemId), ...(consulta ? [consulta.itemId] : [])]);
  const resultadosItem =
    textoItem.length === 0 ? [] : catalogo.filter((i) => !enCarrito.has(i.id) && (i.name.toLowerCase().includes(textoItem) || i.code.toLowerCase().includes(textoItem))).slice(0, 8);

  function agregarItem(item: CatalogoItemCobro): void {
    setLineas((prev) => [...prev, { itemId: item.id, cantidad: '1' }]);
    setBusquedaItem('');
  }

  function quitarItem(itemId: number): void {
    setLineas((prev) => prev.filter((l) => l.itemId !== itemId));
  }

  function cambiarCantidad(itemId: number, cantidad: string): void {
    setLineas((prev) => prev.map((l) => (l.itemId === itemId ? { ...l, cantidad } : l)));
  }

  function cambiarAdministracion(itemId: number, campo: 'administracionDosis' | 'administracionVia' | 'administracionLotCode', valor: string): void {
    setLineas((prev) => prev.map((l) => (l.itemId === itemId ? { ...l, [campo]: valor } : l)));
  }

  async function guardarLineas(): Promise<void> {
    if (!orden) return;
    setGuardando(true);
    setError(undefined);
    setAviso(undefined);
    try {
      const r = await actualizarLineasAtencion(orden.id, lineasAEnviar, specialtyId || undefined);
      if (!r.ok) {
        setError(r.error);
        return;
      }
      setOrden(r.datos.orden);
      setAviso(t('cobroPaciente.lineasGuardadas'));
    } finally {
      setGuardando(false);
    }
  }

  const enviandoRef = useRef(false);

  async function enviarACaja(): Promise<void> {
    if (!orden || enviandoRef.current) return;
    enviandoRef.current = true;
    setEnviando(true);
    setError(undefined);
    setAviso(undefined);
    try {
      const r = await guardarYEnviarACaja(orden.id, lineasAEnviar, specialtyId || undefined);
      if (!r.ok) {
        if (r.recargar && contacto.fase === 'listo') {
          // El servidor agregó o soltó la consulta de la cita y NO firmó: se vuelve a cargar la atención sola y se muestra el total nuevo.
          await iniciarAtencion(contacto.patientId, contacto.phone);
          setAviso(r.error);
          return;
        }
        setError(r.error);
        return;
      }
      setAviso(t('cobroPaciente.enviadaACaja'));
      // La orden ya está firmada, pero el turno de la cita no se pudo cerrar en la Sala: que recepción lo cierre a mano (no es un error de envío).
      if (r.salaCerrada === 'error') setError(t('cobroPaciente.salaNoCerrada'));
      setOrden(null);
      setLineas([]);
      await cargarHistorial(orden.patientId ?? (contacto.fase === 'listo' ? contacto.patientId : 0));
    } finally {
      enviandoRef.current = false;
      setEnviando(false);
    }
  }

  async function confirmarAnulacion(): Promise<void> {
    if (!orden || !motivoAnulacion.trim()) return;
    setAnulando(true);
    setError(undefined);
    setAviso(undefined);
    try {
      const r = await anularAtencion(orden.id, motivoAnulacion.trim());
      if (!r.ok) {
        setError(r.error);
        return;
      }
      setAviso(t('cobroPaciente.atencionAnulada'));
      setOrden(null);
      setLineas([]);
      setMotivoAnulacion('');
      setAnulandoAbierto(false);
      await cargarHistorial(orden.patientId ?? (contacto.fase === 'listo' ? contacto.patientId : 0));
    } finally {
      setAnulando(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <h5 className="text-sm font-semibold">{t('fichaPaciente.tabs.cobro')}</h5>

      {contacto.fase === 'cargando' && <p className="text-sm text-muted-foreground">{t('fichaPaciente.cargando')}</p>}
      {contacto.fase === 'sin-contacto' && (
        <Alert variant="red">
          <AlertDescription>{t('cobroPaciente.sinRegistro')}</AlertDescription>
        </Alert>
      )}
      {contacto.fase === 'error' && (
        <Alert variant="red">
          <AlertDescription>{contacto.mensaje}</AlertDescription>
        </Alert>
      )}

      {contacto.fase === 'listo' && (
        <>
          {especialidades && especialidades.length > 1 && !specialtyId && (
            <div className="max-w-xs">
              <Label className="mb-1 block text-xs">{t('cobroPaciente.especialidad')}</Label>
              <Select value={specialtyId} onValueChange={setSpecialtyId}>
                <SelectTrigger>
                  <SelectValue placeholder={t('agenda.especialidad')} />
                </SelectTrigger>
                <SelectContent>
                  {especialidades.map((e) => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {error && (
            <Alert variant="red">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          {aviso && (
            <Alert>
              <AlertDescription>{aviso}</AlertDescription>
            </Alert>
          )}

          {creandoAtencion && orden === undefined && <p className="text-sm text-muted-foreground">{t('fichaPaciente.cargando')}</p>}

          {orden && (
            <div className="space-y-3 rounded-lg border border-border p-4">
              <p className="text-sm font-semibold text-hce-ink">{t('cobroPaciente.atencionEnCurso')}</p>

              {catalogo.length === 0 ? (
                <p className="text-sm text-hce-muted">{t('cobroPaciente.sinCatalogo')}</p>
              ) : (
                <div className="relative">
                  <Label className="mb-1 block text-xs">{t('cobroPaciente.agregarItem')}</Label>
                  <Input value={busquedaItem} onChange={(e) => setBusquedaItem(e.target.value)} placeholder={t('cobroPaciente.buscarItemPlaceholder')} />
                  {resultadosItem.length > 0 && (
                    <div className="absolute z-10 mt-1 w-full rounded-md border border-border bg-white shadow-md">
                      {resultadosItem.map((item) => (
                        <button key={item.id} type="button" className="block w-full px-3 py-2 text-left text-sm hover:bg-hce-paper" onClick={() => agregarItem(item)}>
                          <span className="block text-hce-ink">{item.name}</span>
                          <span className="block text-xs text-hce-muted">
                            {formatoUsd(item.amountCents)}
                            {item.disponible !== null && ` · ${t('cobroPaciente.disponibleN', { n: item.disponible })}`}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {consulta && <LineaConsultaBloqueada linea={consulta} />}
              {lineas.length === 0 ? (
                consulta ? null : <p className="text-sm text-hce-muted">{t('cobroPaciente.carritoVacio')}</p>
              ) : (
                <div className="divide-y divide-border rounded-lg border border-border">
                  {lineas.map((linea) => {
                    const item = catalogoPorId.get(linea.itemId);
                    if (!item) return null;
                    const cantidadNum = Number(linea.cantidad);
                    const excedeDisponible = item.disponible !== null && Number.isInteger(cantidadNum) && cantidadNum > item.disponible;
                    const esBotiquin = item.farmaciaItemId != null;
                    return (
                      <div key={linea.itemId} className="flex flex-col gap-2 px-3 py-2.5 text-sm">
                        <div className="flex items-center justify-between gap-3">
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-hce-ink">{item.name}</span>
                            <span className="block text-xs text-hce-muted">
                              {formatoUsd(item.amountCents)}
                              {item.disponible !== null && ` · ${t('cobroPaciente.disponibleN', { n: item.disponible })}`}
                            </span>
                            {excedeDisponible && <span className="block text-xs text-destructive">{t('cobroPaciente.excedeDisponible')}</span>}
                          </span>
                          <Input
                            type="number"
                            min={1}
                            value={linea.cantidad}
                            onChange={(e) => cambiarCantidad(linea.itemId, e.target.value)}
                            className="h-9 w-20 shrink-0"
                            aria-label={t('cobroPaciente.cantidadDe', { nombre: item.name })}
                          />
                          <button
                            type="button"
                            onClick={() => quitarItem(linea.itemId)}
                            className="shrink-0 rounded-md p-1.5 text-hce-muted hover:bg-hce-paper hover:text-red-600"
                            aria-label={t('cobroPaciente.quitarItem', { nombre: item.name })}
                          >
                            <X className="size-4" aria-hidden="true" />
                          </button>
                        </div>
                        {esBotiquin && (
                          <div className="grid grid-cols-1 gap-2 rounded-md bg-hce-paper p-2 sm:grid-cols-3">
                            <div>
                              <Label className="mb-1 block text-xs">{t('cobroPaciente.botiquin.dosis')}</Label>
                              <Input
                                value={linea.administracionDosis ?? ''}
                                onChange={(e) => cambiarAdministracion(linea.itemId, 'administracionDosis', e.target.value)}
                                placeholder={t('cobroPaciente.botiquin.dosisPlaceholder')}
                              />
                            </div>
                            <div>
                              <Label className="mb-1 block text-xs">{t('cobroPaciente.botiquin.via')}</Label>
                              <Input
                                value={linea.administracionVia ?? ''}
                                onChange={(e) => cambiarAdministracion(linea.itemId, 'administracionVia', e.target.value)}
                                placeholder={t('cobroPaciente.botiquin.viaPlaceholder')}
                              />
                            </div>
                            <div>
                              <Label className="mb-1 block text-xs">{t('cobroPaciente.botiquin.lote')}</Label>
                              <Select
                                value={linea.administracionLotCode ?? ''}
                                onValueChange={(v) => cambiarAdministracion(linea.itemId, 'administracionLotCode', v)}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder={t('cobroPaciente.botiquin.lotePlaceholder')} />
                                </SelectTrigger>
                                <SelectContent>
                                  {(lotesPorItem[linea.itemId] ?? []).map((lote) => (
                                    <SelectItem key={lote.id} value={lote.lotCode}>
                                      {lote.lotCode} ({lote.quantity})
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {hayCantidadesInvalidas && <p className="text-xs text-destructive">{t('cobroPaciente.cantidadInvalida')}</p>}
              {lineasBotiquinSinAdministracion.length > 0 && (
                <p className="text-xs text-destructive">{t('cobroPaciente.botiquin.faltaAdministracion')}</p>
              )}

              <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
                <span className="text-sm text-hce-muted">
                  {totalServidorCents != null
                    ? t('cobroPaciente.total', { monto: (totalServidorCents / 100).toFixed(2) })
                    : t('cobroPaciente.totalEstimado', { monto: (totalCarritoCents / 100).toFixed(2) })}
                </span>
                <div className="flex gap-2">
                  <Button type="button" variant="secondary" disabled={guardando} onClick={() => void guardarLineas()}>
                    {guardando ? t('cobroPaciente.guardando') : t('cobroPaciente.guardarLineas')}
                  </Button>
                  <Button
                    type="button"
                    disabled={enviando || hayCantidadesInvalidas || lineasAEnviar.length === 0 || lineasBotiquinSinAdministracion.length > 0}
                    onClick={() => void enviarACaja()}
                  >
                    {enviando ? t('cobroPaciente.enviando') : t('cobroPaciente.enviarACaja')}
                  </Button>
                </div>
              </div>

              {!anulandoAbierto ? (
                <button type="button" className="text-xs text-hce-muted hover:text-destructive" onClick={() => setAnulandoAbierto(true)}>
                  {t('cobroPaciente.anular')}
                </button>
              ) : (
                <div className="flex flex-wrap items-end gap-2 rounded-md bg-hce-paper p-2.5">
                  <div className="min-w-48 flex-1">
                    <Label className="mb-1 block text-xs">{t('cobroPaciente.motivoAnulacion')}</Label>
                    <Input value={motivoAnulacion} onChange={(e) => setMotivoAnulacion(e.target.value)} placeholder={t('cobroPaciente.motivoPlaceholder')} />
                  </div>
                  <Button type="button" variant="destructive" size="sm" disabled={anulando || !motivoAnulacion.trim()} onClick={() => void confirmarAnulacion()}>
                    {t('cobroPaciente.confirmarAnulacion')}
                  </Button>
                  <Button type="button" variant="ghost" size="sm" onClick={() => setAnulandoAbierto(false)}>
                    {t('cobroPaciente.cancelar')}
                  </Button>
                </div>
              )}
            </div>
          )}

          {orden === null && !creandoAtencion && (
            <Button
              type="button"
              variant="secondary"
              className="w-fit"
              onClick={() => void iniciarAtencion(contacto.patientId, contacto.phone)}
            >
              {t('cobroPaciente.nuevaAtencion')}
            </Button>
          )}

          <div className="space-y-2">
            <p className="text-sm font-semibold text-hce-ink">{t('cobroPaciente.historialTitulo')}</p>
            {historial.length === 0 ? (
              <p className="text-sm text-hce-muted">{t('cobroPaciente.historialVacio')}</p>
            ) : (
              <div className="divide-y divide-border rounded-lg border border-border">
                {historial.map((h) => (
                  <div key={h.id} className="flex flex-wrap items-center justify-between gap-3 px-3 py-2.5 text-sm">
                    <span className="min-w-0 flex-1">
                      <span className="block text-hce-ink">
                        #{h.id} · {formatoUsd(h.totalCents)} · {t(`cobroPaciente.estado.${h.status}`)}
                        {h.invoiceId ? ` · ${t('cobroPaciente.factura')} #${h.invoiceId}` : ''}
                      </span>
                      <span className="block text-xs text-hce-muted">{h.lines.map((l) => l.description).join(', ') || t('cobroPaciente.sinLineas')}</span>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Sitio de uso real (`routes.tsx` sigue apuntando a `CobroTab`, sin tocar la
 * ruta) — resuelve `family` y delega a `vistaDeFamilia`. Mientras `family`
 * está cargando, renderiza `CobroTabGenerico` (el comportamiento seguro/
 * restringido, sin edición de precio ni descuento): nunca hay un parpadeo a
 * la versión editable de "demo" antes de confirmar que la familia es esa.
 */
export function CobroTab(): JSX.Element {
  const tenantActual = useTenantActual();
  const Vista = tenantActual.estado === 'ok' ? vistaDeFamilia('cobro', tenantActual.tenant.family, CobroTabGenerico) : CobroTabGenerico;
  return <Vista />;
}
